// A site's working copy in the console, and turning it into the next signed bundle.
// The draft holds what we edit (zones, map area, wording, tablets). Nothing in a draft
// reaches a tablet or phone until someone approves the bundle made from it on the site's
// ops tablet.

import { checkBundleShape, LANGUAGE_CODE, LEVELS, MAX_PHRASE, type Level, type Signers, type SiteBundle, type TabletEntry, type Zone } from '../../spec/src/formats.ts';
import { fill, PHRASE_KEYS, phraseProblems, STANDARD_PHRASES, type BlankName, type PhraseKey, type Phrases } from '../../spec/src/phrases.ts';

/** The map picture under the zones: kept by the console, covering exactly the map area. */
export interface MapPicture {
  sha256: string;
  source: 'satellite' | 'client';
  capturedOn: string | null;
  credit: string;
  width: number;
  height: number;
}

export interface SiteDraft {
  zones: Zone[];
  /** Map area shown on the tablet and phones: [west, south, east, north]. */
  map: { bbox: [number, number, number, number] } | null;
  /**
   * The alert instructions per language; the languages staff can choose are exactly
   * these keys. English is always there. A language being filled in may have gaps: it
   * can't be sent for approval until it's complete.
   */
  messages: Record<string, Record<Level, string>>;
  /** Every other word staff see, per language (spec phrases.ts). English and Arabic start from ours. */
  phrases?: Record<string, Partial<Record<string, string>>>;
  /** Languages whose wording a native speaker has checked. */
  checked: string[];
  /** Tablets that can send alerts (and approve the next change) once this is approved. */
  tablets: TabletEntry[];
  /** The map picture, once there is one. */
  picture?: MapPicture | null;
}

/** The product limit (the iPhone's is 20). */
export const MAX_SITE_ZONES = 10;

/**
 * Zones narrower than this still work, but the console warns as soon as one is put in
 * (agreed 29 Sep): phones only know where they are to within about 10-50 m, and near an
 * edge they count themselves as inside, so phones up to about 100 m outside a small
 * zone may get its alerts too.
 */
export const SMALL_ZONE_METRES = 200;

/** A zone's narrowest width in metres (checked every 5 degrees; fine at site scale). */
export function zoneWidthMetres(z: Zone): number {
  const lat0 = z.polygon.reduce((s, p) => s + p[1], 0) / z.polygon.length;
  const lon0 = z.polygon.reduce((s, p) => s + p[0], 0) / z.polygon.length;
  const kx = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  const ky = 110_574;
  const pts = z.polygon.map(([lon, lat]) => [(lon - lon0) * kx, (lat - lat0) * ky]);
  let narrowest = Infinity;
  for (let deg = 0; deg < 180; deg += 5) {
    const a = (deg * Math.PI) / 180;
    const along = pts.map(([x, y]) => x * Math.cos(a) + y * Math.sin(a));
    narrowest = Math.min(narrowest, Math.max(...along) - Math.min(...along));
  }
  return narrowest;
}

/** Zones small enough to warn about, with a plain-words reason. */
export function smallZones(d: Pick<SiteDraft, 'zones'>): { id: string; name: string; metres: number; warning: string }[] {
  return d.zones
    .map((z) => ({ z, metres: Math.round(zoneWidthMetres(z)) }))
    .filter(({ metres }) => metres < SMALL_ZONE_METRES)
    .map(({ z, metres }) => ({
      id: z.id,
      name: z.name,
      metres,
      warning: `Zone ${z.id} · ${z.name} is only about ${metres} m across. Phones up to about 100 m outside it may get its alerts too.`,
    }));
}
const MAX_POINTS = 200;
const MAX_MESSAGE = 200;

/**
 * A site can offer any language (the company decides), but only complete: all five alert
 * instructions and every word of the app. English is always on.
 */
export function languageName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** A real language code (ISO 639: en, ar, pt, sw, fil...). */
export function isLanguage(code: string): boolean {
  if (!LANGUAGE_CODE.test(code)) return false;
  try {
    return new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' }).of(code) !== undefined;
  } catch {
    return false;
  }
}

/** Every word of a language, filled in or not, and what's still to do. */
export function languageProgress(d: Pick<SiteDraft, 'messages' | 'phrases'>, lang: string): { done: number; total: number; complete: boolean } {
  const m = d.messages[lang] ?? ({} as Record<Level, string>);
  const own = d.phrases?.[lang];
  // English and Arabic start from ours, so words we add later are there without anyone typing them.
  const words = lang === 'en' || lang === 'ar' ? { ...STANDARD_PHRASES[lang], ...own } : own;
  const { missing, blanks } = phraseProblems(words);
  const messagesDone = LEVELS.filter((l) => typeof m[l] === 'string' && m[l].trim().length > 0).length;
  const total = LEVELS.length + PHRASE_KEYS.length;
  const done = messagesDone + PHRASE_KEYS.length - missing.length - blanks.length;
  return { done, total, complete: done === total };
}

/** Our standard wording, the starting point for every new site. The Arabic needs a native speaker's check. */
export const STANDARD_WORDING: Record<string, Record<Level, string>> = {
  en: {
    alert: 'Increased threat nearby. Know where your nearest shelter is.',
    shelter: 'Go to shelter immediately.',
    evacuate: 'Leave the site now by your planned route.',
    clear: 'The threat has passed.',
    false_alarm: 'The last alert was a mistake. No action is needed.',
  },
  ar: {
    alert: 'تهديد متزايد في المنطقة. اعرف مكان أقرب ملجأ.',
    shelter: 'توجّه إلى الملجأ فوراً.',
    evacuate: 'غادر الموقع الآن عبر المسار المحدد.',
    clear: 'زال الخطر.',
    false_alarm: 'كان التنبيه السابق خاطئاً. لا حاجة لأي إجراء.',
  },
};

export function emptyDraft(): SiteDraft {
  return { zones: [], map: null, messages: { en: { ...STANDARD_WORDING.en } }, checked: ['en'], tablets: [] };
}

export function draftFromBundle(b: SiteBundle, checked: string[], picture: MapPicture | null = null): SiteDraft {
  return {
    zones: structuredClone(b.zones),
    map: { bbox: [...b.map.bbox] },
    messages: structuredClone(b.messages),
    ...(b.phrases ? { phrases: structuredClone(b.phrases) } : {}),
    checked: checked.filter((l) => l in b.messages),
    tablets: structuredClone(b.tablets),
    picture,
  };
}

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isText = (x: unknown, max: number): x is string => typeof x === 'string' && x.trim().length > 0 && x.length <= max;
const isLonLat = (p: unknown): p is [number, number] =>
  Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90;

/**
 * Checks a draft is well formed (it may still be incomplete). Returns the first problem
 * in plain words, or null.
 */
export function draftProblem(d: unknown): string | null {
  if (!isObj(d)) return 'The draft is not readable.';
  const x = d as Partial<SiteDraft>;
  if (!Array.isArray(x.zones)) return 'Zones are missing.';
  if (x.zones.length > MAX_SITE_ZONES) return `A site can have up to ${MAX_SITE_ZONES} zones.`;
  const ids = new Set<string>();
  for (const z of x.zones) {
    if (!isObj(z) || typeof z.id !== 'string' || !/^(0[1-9]|10)$/.test(z.id)) return 'Zone numbers run from 01 to 10.';
    if (ids.has(z.id)) return `Zone ${z.id} is there twice.`;
    ids.add(z.id);
    if (!isText(z.name, 40)) return `Zone ${z.id} needs a name (up to 40 letters).`;
    if (!Array.isArray(z.polygon) || z.polygon.length < 3 || z.polygon.length > MAX_POINTS || !z.polygon.every(isLonLat)) {
      return `Zone ${z.id} needs an outline of 3 to ${MAX_POINTS} points.`;
    }
  }
  if (x.map !== null) {
    const bb = x.map?.bbox;
    if (!Array.isArray(bb) || bb.length !== 4 || !bb.every(Number.isFinite) || bb[0] >= bb[2] || bb[1] >= bb[3]) return 'The map area is not valid.';
  }
  if (!isObj(x.messages) || !isObj(x.messages.en) || !LEVELS.every((l) => isText((x.messages?.en as Record<string, unknown>)[l], MAX_MESSAGE))) {
    return 'English wording needs every level filled in.';
  }
  for (const [lang, m] of Object.entries(x.messages)) {
    if (!isLanguage(lang)) return `Unknown language: ${lang}.`;
    // A language being filled in may have gaps; it just can't be sent for approval yet.
    if (!isObj(m) || !LEVELS.every((l) => typeof m[l] === 'string' && (m[l] as string).length <= MAX_MESSAGE)) {
      return `${languageName(lang)} alert instructions are up to ${MAX_MESSAGE} letters each.`;
    }
  }
  if (x.phrases !== undefined) {
    if (!isObj(x.phrases)) return 'The app words are not readable.';
    for (const [lang, p] of Object.entries(x.phrases)) {
      if (!(lang in x.messages)) return `There are app words for ${languageName(lang)} but no alert instructions.`;
      if (!isObj(p) || !Object.values(p).every((v) => typeof v === 'string' && v.length <= MAX_PHRASE)) {
        return `${languageName(lang)} app words are up to ${MAX_PHRASE} letters each.`;
      }
    }
  }
  if (!Array.isArray(x.checked) || !x.checked.every((l) => typeof l === 'string')) return 'The wording checks are not readable.';
  if (x.picture !== undefined && x.picture !== null) {
    const pic = x.picture as Partial<MapPicture>;
    if (
      !isObj(pic) ||
      typeof pic.sha256 !== 'string' ||
      !/^[0-9a-f]{64}$/.test(pic.sha256) ||
      (pic.source !== 'satellite' && pic.source !== 'client') ||
      !Number.isInteger(pic.width) ||
      !Number.isInteger(pic.height) ||
      !isText(pic.credit, 200)
    ) {
      return 'The map picture is not readable.';
    }
  }
  if (!Array.isArray(x.tablets)) return 'Tablets are missing.';
  for (const t of x.tablets) {
    if (!isObj(t) || !isText(t.keyId, 64) || !isText(t.publicKey, 100) || !isText(t.label, 80) || typeof t.addedAt !== 'string') {
      return 'A tablet entry is not readable.';
    }
  }
  return null;
}

export interface Readiness {
  /** Things that stop the change being sent for approval. */
  blocking: string[];
  /** Things to know, that don't stop it. */
  warnings: string[];
}

/** "Zone 04", or "Zones 01, 02 and 04". */
export function zoneList(ids: string[]): string {
  if (ids.length === 1) return `Zone ${ids[0]}`;
  return `Zones ${ids.slice(0, -1).join(', ')} and ${ids[ids.length - 1]}`;
}

/** Can this draft be sent to the ops tablet for approval? */
export function readiness(d: SiteDraft): Readiness {
  const blocking: string[] = [];
  const warnings: string[] = [];
  const problem = draftProblem(d);
  if (problem) blocking.push(problem);
  if (d.zones.length === 0) blocking.push('Add at least one zone.');
  if (!d.map) blocking.push('Choose the map area.');
  else {
    const [w, s, e, n] = d.map.bbox;
    const outside = d.zones.filter((z) => z.polygon.some(([lon, lat]) => lon < w || lon > e || lat < s || lat > n));
    if (outside.length) blocking.push(`${zoneList(outside.map((z) => z.id))} ${outside.length === 1 ? 'goes' : 'go'} outside the map area.`);
  }
  if (d.tablets.length === 0) blocking.push('Add the ops tablet: the set-up is approved on it.');
  if (!problem) for (const s of smallZones(d)) warnings.push(s.warning);
  if (d.map && !d.picture) warnings.push('No map picture yet, so the tablet and phones will show the zones on a plain background.');
  if (!problem) {
    for (const lang of Object.keys(d.messages)) {
      const p = languageProgress(d, lang);
      if (!p.complete) {
        const left = p.total - p.done;
        blocking.push(`${languageName(lang)} isn't finished: ${left} of ${p.total} words still to fill in. Finish it, or take it off, before sending.`);
      }
    }
  }
  const unchecked = Object.keys(d.messages).filter((l) => !d.checked.includes(l));
  if (unchecked.length) warnings.push(`Wording not yet checked by someone fluent: ${languageList(unchecked)}.`);
  return { blocking, warnings };
}

/** Builds the next bundle from a draft. Throws if it wouldn't pass spec's checks. */
export function buildBundle(input: {
  siteId: string;
  name: string;
  draft: SiteDraft;
  signers: Signers;
  version: number;
  issuedAt: string;
}): SiteBundle {
  const { draft } = input;
  if (!draft.map) throw new Error('the map area is missing');
  const bundle: SiteBundle = {
    v: 1,
    siteId: input.siteId,
    version: input.version,
    name: input.name,
    zones: [...draft.zones].sort((a, b) => a.id.localeCompare(b.id)).map((z) => ({ id: z.id, name: z.name.trim(), polygon: z.polygon })),
    // The picture travels as its fingerprint; the console only keeps one that covers exactly this map area.
    map: draft.picture
      ? { bbox: draft.map.bbox, picture: { sha256: draft.picture.sha256, width: draft.picture.width, height: draft.picture.height, credit: draft.picture.credit } }
      : { bbox: draft.map.bbox },
    tablets: draft.tablets.map((t) => ({ keyId: t.keyId, publicKey: t.publicKey, label: t.label, addedAt: t.addedAt })),
    messages: Object.fromEntries(Object.entries(draft.messages).map(([l, m]) => [l, Object.fromEntries(LEVELS.map((lv) => [lv, m[lv].trim()]))])) as SiteBundle['messages'],
    ...bundlePhrases(draft),
    signers: input.signers,
    issuedAt: input.issuedAt,
  };
  const shape = checkBundleShape(bundle);
  if (!shape.ok) throw new Error(`bundle would be rejected: ${shape.reason}${shape.detail ? ` (${shape.detail})` : ''}`);
  return bundle;
}

/** The app words that travel in the bundle: every language's own set, but English and Arabic only if changed from ours. */
function bundlePhrases(draft: SiteDraft): { phrases?: SiteBundle['phrases'] } {
  const out: Record<string, Record<string, string>> = {};
  for (const lang of Object.keys(draft.messages)) {
    const standard = lang === 'en' || lang === 'ar' ? STANDARD_PHRASES[lang] : null;
    const own = standard ? { ...standard, ...draft.phrases?.[lang] } : draft.phrases?.[lang];
    if (!own) continue;
    // Trimmed, except the separator between a change's parts, whose spaces matter.
    const trimmed = Object.fromEntries(PHRASE_KEYS.map((k) => [k, k === 'change.separator' ? (own[k] ?? '') : (own[k] ?? '').trim()]));
    if (standard && PHRASE_KEYS.every((k) => trimmed[k] === standard[k])) continue;
    out[lang] = trimmed;
  }
  return Object.keys(out).length ? { phrases: out } : {};
}

/** "English, Arabic and Portuguese": English first, then Arabic, then the rest by name. */
export function languageList(langs: string[]): string {
  const rank = (l: string) => (l === 'en' ? 0 : l === 'ar' ? 1 : 2);
  const names = [...langs].sort((a, b) => rank(a) - rank(b) || languageName(a).localeCompare(languageName(b))).map(languageName);
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const sameOutline = (a: Zone, b: Zone): boolean => JSON.stringify(a.polygon) === JSON.stringify(b.polygon);

/** One thing a change does, before it's put into words (in any language). */
export interface ChangePart {
  key: PhraseKey;
  values: Partial<Record<BlankName, string>>;
  /** A language the part is about, named in whatever language it's read in. */
  language?: string;
  /** The site's languages (set-up only). */
  languages?: string[];
}

/** What a change does, part by part: the log and the ops tablet (before it's approved there) put it into words. */
export function changeParts(prev: SiteBundle | null, next: SiteBundle): ChangePart[] {
  if (!prev) {
    const n = next.zones.length;
    return [{ key: n === 1 ? 'change.siteSetUp.one' : 'change.siteSetUp.many', values: { count: String(n) }, languages: Object.keys(next.messages) }];
  }
  const parts: ChangePart[] = [];
  if (prev.name !== next.name) parts.push({ key: 'change.renameSite', values: { name: next.name } });
  const before = new Map(prev.zones.map((z) => [z.id, z]));
  const after = new Map(next.zones.map((z) => [z.id, z]));
  for (const z of next.zones) {
    const old = before.get(z.id);
    if (!old) parts.push({ key: 'change.addZone', values: { zone: z.id, name: z.name } });
    else {
      if (old.name !== z.name) parts.push({ key: 'change.renameZone', values: { zone: z.id, name: z.name } });
      if (!sameOutline(old, z)) parts.push({ key: 'change.redrawZone', values: { zone: z.id, name: z.name } });
    }
  }
  for (const z of prev.zones) if (!after.has(z.id)) parts.push({ key: 'change.removeZone', values: { zone: z.id, name: z.name } });
  if (JSON.stringify(prev.map.bbox) !== JSON.stringify(next.map.bbox)) parts.push({ key: 'change.mapArea', values: {} });
  if ((prev.map.picture?.sha256 ?? null) !== (next.map.picture?.sha256 ?? null)) parts.push({ key: 'change.mapPicture', values: {} });

  const oldTablets = new Set(prev.tablets.map((t) => t.keyId));
  const newTablets = new Set(next.tablets.map((t) => t.keyId));
  for (const t of next.tablets) if (!oldTablets.has(t.keyId)) parts.push({ key: 'change.addTablet', values: { name: t.label } });
  for (const t of prev.tablets) if (!newTablets.has(t.keyId)) parts.push({ key: 'change.removeTablet', values: { name: t.label } });

  for (const l of Object.keys(next.messages)) {
    // Compared word by word: stored bundles keep their fields in canonical (sorted) order.
    const wordsChanged = PHRASE_KEYS.some((k) => (prev.phrases?.[l]?.[k] ?? null) !== (next.phrases?.[l]?.[k] ?? null));
    if (!(l in prev.messages)) parts.push({ key: 'change.addLanguage', values: {}, language: l });
    else if (wordsChanged || LEVELS.some((lv) => prev.messages[l][lv] !== next.messages[l][lv])) parts.push({ key: 'change.changeWording', values: {}, language: l });
  }
  for (const l of Object.keys(prev.messages)) if (!(l in next.messages)) parts.push({ key: 'change.removeLanguage', values: {}, language: l });
  return parts;
}

/** A language's name, as read in another language: "Portuguese", "البرتغالية", "Português". */
export function languageNameIn(code: string, reader: string): string {
  try {
    return new Intl.DisplayNames([reader], { type: 'language', fallback: 'none' }).of(code) ?? languageName(code);
  } catch {
    return languageName(code);
  }
}

/** The site's languages as a list in the reader's language (English first, then Arabic, then the rest). */
export function languageListIn(codes: string[], reader: string): string {
  if (reader === 'en') return languageList(codes);
  const rank = (l: string) => (l === 'en' ? 0 : l === 'ar' ? 1 : 2);
  const names = [...codes].sort((a, b) => rank(a) - rank(b) || languageName(a).localeCompare(languageName(b))).map((c) => languageNameIn(c, reader));
  try {
    return new Intl.ListFormat(reader, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

/** Puts a change into words, e.g. "Add zone 06 · Loading bay; Add language: Portuguese". */
export function renderChange(parts: ChangePart[], words: Phrases, reader: string): string {
  if (parts.length === 0) return words['change.noChange'];
  const texts = parts.map((p) =>
    fill(words[p.key], {
      ...p.values,
      ...(p.language ? { language: languageNameIn(p.language, reader) } : {}),
      ...(p.languages ? { languages: languageListIn(p.languages, reader) } : {}),
    }),
  );
  const sep = words['change.separator'];
  const text = texts.length > 4 ? texts.slice(0, 3).join(sep) + sep + fill(words['change.more'], { count: String(texts.length - 3) }) : texts.join(sep);
  return text.length > 500 ? `${text.slice(0, 497)}...` : text;
}

/** One line for the log: what this change does, in English, e.g. "Add zone 06 · Loading bay". */
export function describeChange(prev: SiteBundle | null, next: SiteBundle): string {
  return renderChange(changeParts(prev, next), STANDARD_PHRASES.en, 'en');
}
